import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { 
  Zap, 
  Users, 
  Trophy, 
  Clock, 
  Target, 
  Rocket,
  Code2,
  Brain,
  Sparkles
} from 'lucide-react'
import { GlassSphere } from '../three/GlassSphere'

const features = [
  {
    icon: Zap,
    title: 'Learn Faster',
    description: 'Our AI-powered platform adapts to your learning style, helping you master concepts 2x faster than traditional methods.',
    stat: '2x',
    statLabel: 'Faster Learning',
    color: '#5ed29c',
  },
  {
    icon: Users,
    title: 'Join the Community',
    description: 'Connect with 50,000+ developers worldwide. Share knowledge, collaborate on projects, and grow together.',
    stat: '50K+',
    statLabel: 'Active Members',
    color: '#4fd8ff',
  },
  {
    icon: Trophy,
    title: 'Industry Recognition',
    description: 'Our certificates are recognized by top tech companies. 89% of our graduates land jobs within 6 months.',
    stat: '89%',
    statLabel: 'Job Placement',
    color: '#8b5cf6',
  },
  {
    icon: Clock,
    title: 'Flexible Schedule',
    description: 'Learn at your own pace. Access courses 24/7 from anywhere in the world. No deadlines, no pressure.',
    stat: '24/7',
    statLabel: 'Access',
    color: '#5ed29c',
  },
  {
    icon: Target,
    title: 'Project-Based',
    description: 'Build real projects for your portfolio. Every course includes hands-on projects that solve real problems.',
    stat: '100+',
    statLabel: 'Real Projects',
    color: '#4fd8ff',
  },
  {
    icon: Rocket,
    title: 'Career Support',
    description: 'Get personalized career guidance, resume reviews, and interview preparation from industry experts.',
    stat: '1:1',
    statLabel: 'Mentorship',
    color: '#8b5cf6',
  },
]

const comparisonData = [
  { feature: 'Interactive Learning', codenest: true, others: false },
  { feature: 'AI-Powered Feedback', codenest: true, others: false },
  { feature: 'Real Projects', codenest: true, others: true },
  { feature: 'Community Support', codenest: true, others: true },
  { feature: '1:1 Mentorship', codenest: true, others: false },
  { feature: 'Job Guarantee', codenest: true, others: false },
]

export function WhyCodeNest() {
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: true, margin: '-100px' })

  return (
    <section
      ref={containerRef}
      id="why-codenest"
      className="relative py-32 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 aurora-bg opacity-30" />
      <div className="absolute top-1/2 right-0 w-1/2 h-1/2 -translate-y-1/2 opacity-50 hidden lg:block">
        <GlassSphere />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-white/80">Why Choose Us</span>
          </motion.div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Why <span className="text-gradient">CodeNest</span>?
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            We do not just teach code. We transform you into a confident developer 
            ready to tackle real-world challenges.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="group relative glass rounded-2xl p-8 overflow-hidden"
            >
              {/* Hover Glow */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: `radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${feature.color}10, transparent 40%)`
                }}
              />
              
              <div className="relative z-10">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-gradient transition-all duration-300">
                  {feature.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  {feature.description}
                </p>
                
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold" style={{ color: feature.color }}>
                    {feature.stat}
                  </span>
                  <span className="text-sm text-white/40">{feature.statLabel}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="glass rounded-3xl p-8 lg:p-12"
        >
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">How We Compare</h3>
            <p className="text-white/60">See why developers choose CodeNest over traditional platforms</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-white/40 font-medium">Feature</th>
                  <th className="text-center py-4 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Code2 className="w-5 h-5 text-primary" />
                      <span className="font-semibold text-white">CodeNest</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4 text-white/40 font-medium">Others</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <motion.tr
                    key={row.feature}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    className="border-b border-white/5 last:border-0"
                  >
                    <td className="py-4 px-4 text-white/80">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {row.codenest ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/20">
                          <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <span className="text-white/20">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.others ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10">
                          <svg className="w-5 h-5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <span className="text-white/20">—</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 text-center">
            <motion.a
              href="#courses"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-background font-semibold hover:bg-white/90 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Brain className="w-5 h-5" />
              Start Your Journey
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}